import { Injectable, inject } from "@angular/core";
import { ContactService } from "@app/shared/services/contact.service";
import { LocalStoreService } from "@app/shared/services/local-store.service";
import { CompaniesType } from "@arch/domain/entity";
import { AccountingType } from "@arch/domain/entity/accounting.entity";
import { environment } from "@environments/environment";
import mixpanel from "mixpanel-browser";

@Injectable({
    providedIn: "root"
})
export class EventsService {
    private params: any;
    private mixpanelReady = false;

    private readonly contactService = inject(ContactService);
    private readonly ls = inject(LocalStoreService);

    private isValidMixpanelToken(token: string): boolean {
        return Boolean(token && token !== "__MXP_TKE__" && token.trim().length > 0);
    }

    private safeMixpanelCall(action: string, fn: () => void): void {
        if (!this.mixpanelReady) return;
        try {
            fn();
        } catch (error) {
            console.error(`[Events] Mixpanel ${action} failed`, error);
        }
    }

    public send(eventName: string): boolean {
        if (!environment.production) return false;
        try {
            const contact = this.contactService.get();
            if (contact) {
                const rolesAllowed = [
                    "Contabilidade",
                    "ContabilidadeTrial",
                    "ContabilidadeTrialPro",
                    "Contabil",
                    "Comercial",
                    "Fiscal",
                    "Pessoal",
                    "Empresa",
                    "Financeiro",
                    "Gestor",
                    "Pessoal e Fiscal",
                    "Convidado",
                    "Contato"
                ];
                const hasPermission = contact.checkPermission(rolesAllowed);
                if (hasPermission) {
                    const company = this.ls.getItem("company") as CompaniesType;
                    const accounting = this.ls.getItem(
                        "accounting"
                    ) as AccountingType;
                    const params: any = {
                        ...accounting,
                        ...company,
                        accountingName: company?.accounting?.name,
                        accountingId:
                            contact.props.accountingId ||
                            company?.accounting?._id ||
                            company?.accountingId ||
                            accounting?._id,
                        contact: contact?.getProps() || {}
                    };
                    delete params.name;
                    if (company?._id) {
                        params.companyName = company?.name;
                        params.companyId = company?._id;
                    }
                    this.event(eventName, params);
                    return true;
                } else {
                    return false;
                }
            }
            return false;
        } catch (error) {
            console.error("Events", error);
            return false;
        }
    }

    public event(event: string, fields: any) {
        const eventName = event
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

        this.safeMixpanelCall("track", () => {
            mixpanel.track(eventName, {
                ...this.params,
                ...fields
            });
        });
    }

    public init(token: string) {
        if (!environment.production || !this.isValidMixpanelToken(token)) return;

        try {
            mixpanel.init(token, { debug: false });
            this.mixpanelReady = true;
        } catch (error) {
            this.mixpanelReady = false;
            console.error("[Events] Mixpanel init failed", error);
        }
    }

    public identify({
        _id,
        email,
        type
    }: {
        _id: string;
        email: string;
        type: string;
    }) {
        if (!environment.production) return;

        this.safeMixpanelCall("identify", () => {
            mixpanel.identify(_id);
            mixpanel.people.set({
                email: email,
                type: type
            });
        });
    }
}
